import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Video, Play, BarChart2, Plus, Clock, ChevronRight, 
  Trophy, Calendar, TrendingUp, ArrowLeft, History, Sun, Droplets, Apple,
  FileText, GitCompare, Trash2, X
} from 'lucide-react';

import GameSetup from './GameSetup';
import TallyDashboard from './TallyDashboard';
import MatchSummaryCard from './MatchSummaryCard';
import AIFeedbackCard from './AIFeedbackCard';
import PlayerAnalytics from './PlayerAnalytics';
import SeasonAveragesCard from './SeasonAveragesCard';
import CompareGames from './CompareGames';
import StatCard from './StatCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoReviewModule = ({ defaultDate, onClose, onSwitchToDashboard }) => {
  const [view, setView] = useState('home'); // home, setup, tally, summary, feedback, analytics, compare, history
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [completedStats, setCompletedStats] = useState(null);
  const [averagesKey, setAveragesKey] = useState(0);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/game-sessions?limit=10`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionCreated = (session) => {
    setActiveSession(session);
    setView('tally');
  };

  const handleSessionComplete = (data) => {
    // Store feedback data and stats for later
    setFeedbackData(data);
    setCompletedStats(data.stats || activeSession?.stats || {});
    
    // Update active session with completed data
    setActiveSession(prev => ({
      ...prev,
      ...data.session,
      is_complete: true,
      points_earned: data.points_earned,
      duration_minutes: data.duration_minutes
    }));
    
    // Go to summary first, then user can view AI feedback
    setView('summary');
    fetchSessions(); // Refresh sessions list
  };

  const handleViewFeedback = () => {
    setView('feedback');
  };

  const handleContinueToDashboard = () => {
    setView('home');
    setActiveSession(null);
    setFeedbackData(null);
    setCompletedStats(null);
    fetchSessions(); // Refresh sessions when returning to home
  };

  const handleBackToHome = () => {
    setView('home');
    setActiveSession(null);
    setCompletedStats(null);
    fetchSessions(); // Refresh sessions when going back
  };

  const handleResumeSession = (session) => {
    if (session.is_complete) {
      // View completed session
      setActiveSession(session);
      setFeedbackData({
        ai_feedback: session.ai_feedback,
        derived_stats: session.derived_stats,
        stat_labels: session.stat_labels
      });
      setView('feedback');
    } else {
      // Resume incomplete session
      setActiveSession(session);
      setView('tally');
    }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    
    if (!window.confirm('Are you sure you want to delete this session? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/game-sessions/${sessionId}`);
      toast.success('Session deleted');
      fetchSessions(); // Refresh the list
      setAveragesKey(k => k + 1); // Season averages include this session, so remount to refetch
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete session');
    }
  };

  const formatPosition = (pos) => {
    return pos?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'N/A';
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'bg-slate-500';
    if (grade.startsWith('A')) return 'bg-green-500';
    if (grade.startsWith('B')) return 'bg-blue-500';
    if (grade.startsWith('C')) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Render based on current view
  if (view === 'setup') {
    return (
      <GameSetup 
        defaultDate={defaultDate}
        onSessionCreated={handleSessionCreated}
        onBack={handleBackToHome}
      />
    );
  }

  if (view === 'tally' && activeSession) {
    return (
      <TallyDashboard 
        session={activeSession}
        onComplete={handleSessionComplete}
        onBack={handleBackToHome}
      />
    );
  }

  if (view === 'summary' && activeSession) {
    return (
      <MatchSummaryCard 
        session={activeSession}
        stats={completedStats}
        statLabels={feedbackData?.stat_labels}
        onViewFeedback={handleViewFeedback}
        onBackToHome={handleBackToHome}
      />
    );
  }

  if (view === 'feedback' && feedbackData) {
    return (
      <AIFeedbackCard 
        feedback={feedbackData.ai_feedback}
        derivedStats={feedbackData.derived_stats}
        onContinue={handleContinueToDashboard}
      />
    );
  }

  if (view === 'analytics') {
    return (
      <PlayerAnalytics onBack={() => setView('home')} />
    );
  }

  if (view === 'compare') {
    return (
      <CompareGames onBack={() => setView('home')} />
    );
  }

  if (view === 'statcard') {
    return (
      <StatCard onBack={() => setView('home')} />
    );
  }

  // Home View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <Button 
            variant="ghost" 
            className="text-slate-400 hover:text-white"
            onClick={onClose}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Module Header */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Video Review & Stats</CardTitle>
                <CardDescription className="text-slate-400">
                  Track your game performance and get AI feedback
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            className="h-auto py-6 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 flex flex-col items-center gap-2"
            onClick={() => setView('setup')}
            data-testid="new-session-btn"
          >
            <Plus className="w-6 h-6" />
            <span className="font-semibold">New Session</span>
            <span className="text-xs opacity-80">Start tracking stats</span>
          </Button>
          
          <Button
            className="h-auto py-6 bg-gradient-to-br from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 flex flex-col items-center gap-2"
            onClick={() => setView('analytics')}
            data-testid="view-analytics-btn"
          >
            <BarChart2 className="w-6 h-6" />
            <span className="font-semibold">Analytics</span>
            <span className="text-xs opacity-80">View performance trends</span>
          </Button>
        </div>

        {/* Secondary Actions: Compare & Export */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-auto py-4 border-slate-600 bg-slate-800/50 hover:bg-slate-700 text-white flex flex-col items-center gap-1"
            onClick={() => setView('compare')}
            data-testid="compare-games-btn"
          >
            <GitCompare className="w-5 h-5 text-purple-400" />
            <span className="font-medium text-sm">Compare Games</span>
          </Button>
          
          <Button
            variant="outline"
            className="h-auto py-4 border-slate-600 bg-slate-800/50 hover:bg-slate-700 text-white flex flex-col items-center gap-1"
            onClick={() => setView('statcard')}
            disabled={sessions.filter(s => s.is_complete).length === 0}
            data-testid="recruiter-card-btn"
          >
            <FileText className="w-5 h-5 text-amber-400" />
            <span className="font-medium text-sm">Recruiter Card</span>
          </Button>
        </div>

        {/* Season Averages Card */}
        <SeasonAveragesCard key={averagesKey} compact />

        {/* In Progress Sessions */}
        {sessions.filter(s => !s.is_complete).length > 0 && (
          <Card className="bg-slate-800/90 border-slate-700 border-amber-500/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Continue Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {sessions.filter(s => !s.is_complete).map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center gap-2"
                  >
                    <button
                      onClick={() => handleResumeSession(session)}
                      className="flex-1 flex items-center justify-between p-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg transition-colors"
                      data-testid={`continue-session-${session.id}`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-white">vs. {session.opponent_name}</div>
                        <div className="text-sm text-slate-400">
                          {session.game_date} • {formatPosition(session.position)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-amber-500 text-amber-400">
                          {session.stat_events?.length || 0} plays
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-amber-400" />
                      </div>
                    </button>
                    <button
                      onClick={(e) => handleDeleteSession(e, session.id)}
                      className="p-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-colors text-red-400 hover:text-red-300"
                      title="Delete session"
                      data-testid={`delete-session-${session.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Sessions */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" />
              Recent Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-slate-400 py-4">Loading sessions...</div>
            ) : sessions.filter(s => s.is_complete).length === 0 ? (
              <div className="text-slate-400 py-4 text-center">
                No completed sessions yet. Start your first video review!
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.filter(s => s.is_complete).slice(0, 5).map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center gap-2"
                  >
                    <button
                      onClick={() => handleResumeSession(session)}
                      className="flex-1 flex items-center justify-between p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                      data-testid={`view-session-${session.id}`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-white">vs. {session.opponent_name}</div>
                        <div className="text-sm text-slate-400">
                          {session.game_date} • {formatPosition(session.position)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {session.ai_feedback && (
                          <Badge className={`${getGradeColor(session.ai_feedback.grade)} text-white`}>
                            {session.ai_feedback.grade}
                          </Badge>
                        )}
                        {session.coach_feedback?.length > 0 && (
                          <Badge variant="outline" className="border-purple-500 text-purple-400">
                            {session.coach_feedback.length} notes
                          </Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </div>
                    </button>
                    <button
                      onClick={(e) => handleDeleteSession(e, session.id)}
                      className="p-3 bg-slate-700/50 hover:bg-red-500/20 border border-slate-600 hover:border-red-500/30 rounded-lg transition-colors text-slate-400 hover:text-red-400"
                      title="Delete session"
                      data-testid={`delete-completed-${session.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Switch to Regular Dashboard */}
        <div className="border-t border-slate-700 pt-6 mt-6">
          <button
            onClick={onSwitchToDashboard || onClose}
            className="w-full p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-amber-500/50 hover:bg-slate-800 transition-all group"
            data-testid="switch-to-dashboard-btn"
          >
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium group-hover:text-amber-400 transition-colors">
                  Switch to Today's Dashboard
                </p>
                <p className="text-slate-400 text-sm">
                  Track hydration, nutrition, sleep & activities
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoReviewModule;
